import type { UseQueryResult } from '@tanstack/react-query';
import Decimal from 'decimal.js-light';
import { useMemo } from 'react';
import { formatUnits } from 'viem';

import type { CrossChainQuote } from '@/lib/api/0x-cross-chain/types';

import {
  calculateGrossBuyAmountCrossChain,
  getQuoteParams,
} from '@/components/TransactionModule/CrossChainMode/utils';

import { CHAIN_IDS } from '@/constants/chain';

import { CROSS_CHAIN_QUOTE_REFRESH_MS, useCrossChainQuotes } from '@/hooks/0xCrossChain/useQuotes';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useCrossChainResolvedSlippage } from '@/hooks/useResolvedSlippage';
import { useTokenBalance } from '@/hooks/useTokenBalance';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import { type ChainAddress } from '@/utils/models';
import { isTokenAddressNativeAsset } from '@/utils/multichain';

import { emptyQuoteFields, type CrossChainTradeQuote, type TokenUsdPrices } from './tradeQuote';

export type CrossChainTradeQuoteParams = {
  /** False while another source (market/limit) owns the trade; no cross-chain fetch fires. */
  enabled: boolean;
  /** Whether a pair and a positive sell amount are selected. */
  hasTradeInputs: boolean;
  sellToken: TokenResult | undefined;
  buyToken: TokenResult | undefined;
  /** The immediate sell amount; the served payload must belong to it. */
  sellAmount: string | undefined;
  /** The debounced sell amount the lane fetches against. */
  debouncedSellAmount: string | undefined;
  sellInputUsd: number;
  tokenUsdPrices: TokenUsdPrices;
  /** Pasted buy-side recipient; undefined sends to the connected wallet on the buy chain. */
  recipientAddress: ChainAddress | undefined;
};

/**
 * Cross-chain lane provider: owns wallet/recipient resolution, the quote
 * request, and the one-shot native-gas probe, and normalizes the response into
 * a `CrossChainTradeQuote`. While the debounced amount trails the immediate
 * input, the previous payload is reported as loading/stale instead of fetched.
 */
export function useCrossChainTradeQuote({
  enabled,
  hasTradeInputs,
  sellToken,
  buyToken,
  sellAmount,
  debouncedSellAmount,
  sellInputUsd,
  tokenUsdPrices,
  recipientAddress,
}: CrossChainTradeQuoteParams): {
  quote: CrossChainTradeQuote;
  gasProbeQuote: CrossChainQuote | undefined;
  refetchQuote: UseQueryResult<CrossChainQuote | undefined, Error>['refetch'];
} {
  const { ethereumWallet, solanaWallet } = useMatchaWallets();
  const {
    bps: crossChainSlippageBps,
    isReady: isSlippageReady,
    isAuto: isAutoSlippage,
  } = useCrossChainResolvedSlippage({ sellToken, buyToken });

  const sellSideWallet = sellToken?.chainId === CHAIN_IDS.SOLANA ? solanaWallet : ethereumWallet;
  const buySideWallet =
    buyToken && !recipientAddress
      ? buyToken.chainId === CHAIN_IDS.SOLANA
        ? solanaWallet
        : ethereumWallet
      : undefined;
  const sellSideWalletAddress = sellSideWallet?.address as ChainAddress | undefined;
  const buySideWalletAddress = buySideWallet
    ? (buySideWallet.address as ChainAddress)
    : recipientAddress;

  const quoteParams = useMemo(
    () =>
      enabled && isSlippageReady
        ? getQuoteParams(
            sellToken,
            buyToken,
            debouncedSellAmount,
            sellSideWalletAddress,
            buySideWalletAddress,
            crossChainSlippageBps,
            isAutoSlippage ? 'auto' : 'manual',
            sellInputUsd,
          )
        : undefined,
    [
      buySideWalletAddress,
      buyToken,
      debouncedSellAmount,
      enabled,
      isSlippageReady,
      isAutoSlippage,
      sellInputUsd,
      sellSideWalletAddress,
      sellToken,
      crossChainSlippageBps,
    ],
  );
  // Limit retention of unused selections, as in the market lane.
  const result = useCrossChainQuotes(quoteParams, { gcTime: CROSS_CHAIN_QUOTE_REFRESH_MS });

  const { data: sellTokenBalance } = useTokenBalance({
    address: sellSideWallet?.address,
    tokenAddress: sellToken?.address,
    chainId: sellToken?.chainId,
  });
  const sellTokenBalanceUnits = sellTokenBalance?.value;
  const gasProbeParams = useMemo(() => {
    if (
      !enabled ||
      !isSlippageReady ||
      !sellToken ||
      !buyToken ||
      !isTokenAddressNativeAsset(sellToken.address, sellToken.chainId) ||
      debouncedSellAmount ||
      sellToken.chainId === buyToken.chainId ||
      sellTokenBalanceUnits == null ||
      sellTokenBalanceUnits <= 0n
    ) {
      return undefined;
    }

    return getQuoteParams(
      sellToken,
      buyToken,
      sellTokenBalanceUnits.toString(),
      sellSideWalletAddress,
      buySideWalletAddress,
      crossChainSlippageBps,
      isAutoSlippage ? 'auto' : 'manual',
      undefined,
    );
  }, [
    buySideWalletAddress,
    buyToken,
    debouncedSellAmount,
    enabled,
    isSlippageReady,
    isAutoSlippage,
    sellSideWalletAddress,
    sellToken,
    sellTokenBalanceUnits,
    crossChainSlippageBps,
  ]);
  const { data: gasProbeQuote } = useCrossChainQuotes(gasProbeParams, {
    refetchInterval: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
  });

  const isDebounceSettled = debouncedSellAmount === sellAmount;

  const quote = useMemo<CrossChainTradeQuote>(() => {
    const idle = (error?: Error): CrossChainTradeQuote => ({
      ...emptyQuoteFields(error),
      source: 'cross-chain',
      status: error ? 'error' : 'idle',
    });

    if (!hasTradeInputs || !sellToken || !buyToken) return idle();
    const error = result.error ?? undefined;
    if (error) return idle(error);
    const raw = result.data;
    if (!raw || !isDebounceSettled) {
      return { ...idle(), status: 'loading', isStale: Boolean(raw) };
    }
    const buyAmount = calculateGrossBuyAmountCrossChain({ quote: raw, sellToken, buyToken });
    const grossBuyAmountDecimal = formatUnits(BigInt(buyAmount), buyToken.decimals);
    const buyAmountUsd = new Decimal(grossBuyAmountDecimal).mul(tokenUsdPrices.buyTokenUsd);
    const priceImpact =
      sellInputUsd > 0
        ? {
            percent: buyAmountUsd.div(sellInputUsd).sub(1).mul(100).toNumber(),
            usd: buyAmountUsd.sub(sellInputUsd).toNumber(),
          }
        : undefined;
    return {
      source: 'cross-chain',
      status: 'fetched',
      buyAmount,
      priceImpact,
      fee: undefined,
      isStale: result.isFetching,
      raw,
      error: undefined,
      quoteError: undefined,
    };
  }, [
    buyToken,
    hasTradeInputs,
    isDebounceSettled,
    result.data,
    result.error,
    result.isFetching,
    sellInputUsd,
    sellToken,
    tokenUsdPrices,
  ]);

  return { quote, gasProbeQuote, refetchQuote: result.refetch };
}
